import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, X, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
  onClick: () => void;
}

const DISMISS_KEY = "setup-checklist-dismissed";

/**
 * Sidebar "Finish setup" widget shown during the first-days of a new trainer's
 * journey. Reads state from the DB so checkmarks are source-of-truth, not a
 * stored flag.
 *
 * Auto-hides once all items are complete OR if the user explicitly dismisses.
 */
export function SetupChecklist() {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [hasClient, setHasClient] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [hasAvailability, setHasAvailability] = useState(false);
  const [hasCalendar, setHasCalendar] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"
  );

  const refresh = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const [
      clientsRes,
      sessionsRes,
      availabilityRes,
      calendarRes,
    ] = await Promise.all([
      supabase
        .from("clients")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("sessions")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("trainer_availability")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("google_calendar_tokens")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle(),
    ]);

    setHasClient((clientsRes.count ?? 0) > 0);
    setHasSession((sessionsRes.count ?? 0) > 0);
    setHasAvailability((availabilityRes.count ?? 0) > 0);
    setHasCalendar(!!calendarRes.data);

    setLoaded(true);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const items: ChecklistItem[] = [
    {
      id: "client",
      label: "Add your first client",
      done: hasClient,
      onClick: () => navigate("/clients?add=1"),
    },
    {
      id: "session",
      label: "Book your first session",
      done: hasSession,
      onClick: () => navigate("/calendar?schedule=1"),
    },
    {
      id: "availability",
      label: "Set weekly availability",
      done: hasAvailability,
      onClick: () => navigate("/settings?tab=availability"),
    },
    {
      id: "calendar",
      label: "Connect your calendar",
      done: hasCalendar,
      onClick: () => navigate("/settings?tab=calendar"),
    },
    // Billing model removed from onboarding (2026-07-07): it's back-office
    // financial config, not a getting-started essential. It was also detected
    // via a fragile single-org lookup that broke for trainers in >1 org. Set it
    // in Settings → Billing when/if wanted; it no longer gates "setup complete".
  ];

  const doneCount = items.filter((i) => i.done).length;
  const allDone = doneCount === items.length;

  // Don't render at all if not yet loaded, dismissed, or everything is complete.
  if (!loaded || dismissed || allDone) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mx-3 mb-3 rounded-xl border border-border/60 bg-card/80 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
            Finish setup
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-muted-foreground">
            {doneCount}/{items.length}
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded p-0.5 text-muted-foreground/60 hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
            aria-label="Dismiss setup checklist"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Thin progress bar */}
      <div className="h-1 w-full rounded-full bg-secondary/60 overflow-hidden mb-2.5">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${(doneCount / items.length) * 100}%` }}
        />
      </div>

      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={item.onClick}
              className={cn(
                "group flex items-center w-full gap-2 rounded-md px-1.5 py-1 text-left transition-colors",
                "hover:bg-accent/[0.06]"
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 items-center justify-center rounded-full border shrink-0 transition-colors",
                  item.done
                    ? "bg-primary border-primary text-primary-foreground"
                    : "border-border bg-background"
                )}
              >
                {item.done && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span
                className={cn(
                  "flex-1 text-[12px] font-medium tracking-tight",
                  item.done
                    ? "text-muted-foreground line-through decoration-muted-foreground/40"
                    : "text-foreground"
                )}
              >
                {item.label}
              </span>
              {!item.done && (
                <ChevronRight className="h-3 w-3 text-muted-foreground/50 group-hover:text-accent transition-colors shrink-0" />
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
